import { apiRequest } from '../lib/httpClient'
import type { McpAccessPerson, McpConnection, McpMyAccess, McpRevokeReason } from '@/types/mcp'
import { asArray, pickBool, pickNumber, pickString } from './shared/coercion'
import { toMcpClient } from './shared/mcp'

const REVOKE_REASONS: readonly McpRevokeReason[] = ['user', 'admin', 'accessRemoved', 'replay']

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
}

const optionalString = (value: unknown) => pickString(value) || null

function toPerson(raw: unknown): McpAccessPerson {
  const record = asRecord(raw)
  return {
    userId: pickString(record['userId']),
    name: pickString(record['name']),
    email: pickString(record['email']),
    hasAccess: pickBool(record['hasAccess']),
    grantedBy: pickString(record['grantedBy']),
    grantedAt: optionalString(record['grantedAt']),
    liveConnections: pickNumber(record['liveConnections']),
  }
}

function toRevokeReason(value: unknown): McpRevokeReason | null {
  const reason = pickString(value)
  return REVOKE_REASONS.find((known) => known.toLowerCase() === reason.toLowerCase()) ?? null
}

function toConnection(raw: unknown): McpConnection {
  const record = asRecord(raw)
  const revokedAt = optionalString(record['revokedAt'])
  const expiresAt = pickString(record['expiresAt'])
  return {
    connectionId: pickString(record['connectionId']),
    userId: pickString(record['userId']),
    userName: pickString(record['userName']),
    clientId: pickString(record['clientId']),
    clientName: pickString(record['clientName']),
    appName: pickString(record['appName']),
    redirectHost: pickString(record['redirectHost']),
    connectedAt: pickString(record['connectedAt']),
    lastUsedAt: optionalString(record['lastUsedAt']),
    expiresAt,
    // The server's clock decides; the browser's can be wrong, and Disconnect on an ended row is refused.
    isLive: pickBool(record['isLive'], !revokedAt && Date.parse(expiresAt) > Date.now()),
    revokedAt,
    revokedBy: pickString(record['revokedBy']),
    revokeReason: toRevokeReason(record['revokeReason']),
  }
}

const toConnections = (payload: unknown) =>
  asArray<unknown>(payload).map(toConnection).filter((connection) => connection.connectionId)

export async function fetchMcpCanManage(clientId: string): Promise<boolean> {
  return (await apiRequest<unknown>('Mcp/canManage', { method: 'GET', query: { clientId } })) === true
}

export async function fetchMcpAccess(clientId: string): Promise<McpAccessPerson[]> {
  const payload = await apiRequest<unknown>('Mcp/access', { method: 'GET', query: { clientId } })
  return asArray<unknown>(payload)
    .map(toPerson)
    .filter((person) => person.userId)
}

export async function grantMcpAccess(clientId: string, userIds: string[]): Promise<void> {
  await apiRequest<boolean>('Mcp/access', { method: 'POST', body: { clientId, userIds } })
}

export async function removeMcpAccess(clientId: string, userId: string): Promise<void> {
  await apiRequest<boolean>('Mcp/access', { method: 'DELETE', query: { clientId, userId } })
}

export async function fetchMcpConnections(clientId: string): Promise<McpConnection[]> {
  return toConnections(await apiRequest<unknown>('Mcp/connections', { method: 'GET', query: { clientId } }))
}

export async function fetchSupportMcpConnections(): Promise<McpConnection[]> {
  return toConnections(
    await apiRequest<unknown>('Mcp/connections', { method: 'GET', query: { server: 'support' } })
  )
}

export async function revokeMcpConnection(connectionId: string): Promise<void> {
  await apiRequest<boolean>(`Mcp/connections/${encodeURIComponent(connectionId)}`, { method: 'DELETE' })
}

export async function fetchMyMcpAccess(): Promise<McpMyAccess> {
  const record = asRecord(await apiRequest<unknown>('Mcp/myAccess', { method: 'GET' }))
  const clients = asArray<unknown>(record['clients']).map(toMcpClient).filter((client) => client.clientId)
  const clientNames = new Map(clients.map((client) => [client.clientId, client.name]))
  return {
    clients,
    connectorUrl: pickString(record['connectorUrl']),
    connections: toConnections(record['connections']).map((connection) => ({
      ...connection,
      clientName: connection.clientName || clientNames.get(connection.clientId) || '',
    })),
  }
}
